import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import { ProductionColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { CloseOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { FaFileAlt } from "react-icons/fa";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { downloadProductionCSV } from "../utils/downloadProductionReport";
import { downloadProductionPDF } from "../utils/downloadProductionReportPdf";
import { hasRole } from "../../../global/hooks/getUserRole";
import CustomDateTimePicker from "../../../global/components/inputWidgets/CustomDatePicker";
import { convertToUnix } from "../../../global/utils/unixToLocalTime";
import dayjs from "dayjs";
import { PRODUCTION_LIST_SEARCH_KEY } from "../utils/listSearchStorage";

const initialFilterState = {
  ProductionNo: "",
  ProductionTime: "",
  // OemNo: "",
  ProductionName: "",
};

export const Production = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "ProductionNo",
    order: "desc",
  });

  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //

  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [showModal, setShowModal] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reportFile, setReportFile] = useState(1);
  const [form] = Form.useForm();
  const [formFilter] = Form.useForm();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    sessionStorage.setItem(PRODUCTION_LIST_SEARCH_KEY, location.search || "");
  }, [location.search]);

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/productions/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/productions/`;
  const { data, loading, error, refetch } = useGet(url);

  const csvQuery = query;
  csvQuery.delete("limit");
  csvQuery.append("production_ids", selectedRowKeys);
  const updatedCsvQuery = csvQuery.toString();
  const exportProduction = updatedCsvQuery
    ? `${process.env.REACT_APP_API_URL}/api/productions/export/?${updatedCsvQuery}`
    : `${process.env.REACT_APP_API_URL}/api/productions/export/`;
  const { refetch: refetchExport, loading: exportLoading } = useGet(
    shouldFetch ? exportProduction : null,
  );

  // const {
  //   data: optionData,
  //   loading: optionDataLoading,
  //   error: optionDataError,
  // } = useGet(optionUrl);
  // const modifiedOems = getModifiedResponse(optionData, "oems");
  // const modifiedBillingCycles = getModifiedResponse(optionData, "bill_cycles");
  // const modifiedAutoBilling = getModifiedResponse(optionData, "auto_billings");

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/productions/bulk-delete/`)
    : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/productions/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(`/production/info/${record?.ProductionNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      )
        if (key === "ProductionTime") {
          filterDict[key] = convertToUnix(filter[key]);
        } else {
          filterDict[key] = filter[key];
        }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "ProductionNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "ProductionNo", // Make sure to specify sorting if necessary
      order: "desc",
    };
    setParams(params);
    refetch();
    setSelectedRowKeys([]);
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");

      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.ProductionNo,
      }));
      setListData(newData);
    }
    // setSelectedRowKeys([]);
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Production",
      href: "/production",
    },
  ];
  const handleExportClick = async (format = 1) => {
    try {
      const freshData = await refetchExport();

      if (!freshData) {
        throw new Error("No export data found");
      }
      const timestamp = new Date().toISOString().split("T")[0];
      if (format === 1) {
        const filename = `Production-Report_${timestamp}.csv`;
        downloadProductionCSV(freshData, filename);
      } else if (format === 2) {
        const filename = `Production-Report_${timestamp}.pdf`;
        downloadProductionPDF(freshData, filename);
      }
      openConfirmNotification(`Success`);
    } catch (error) {
      console.error("Export error:", error);
      openErrorNotification(error.message || "Something went wrong.");
    }
    setShouldFetch(false);
  };

  const handleFormChange = (changedValues, allValues) => {
    // Update filter state when form values change
    Object.keys(changedValues).forEach((key) => {
      if (key === "ProductionTime") {
        setFilter({ ...filter, ProductionTime: changedValues[key] });
      }
    });
  };

  useEffect(() => {
    const formValues = {};

    if (modParams?.ProductionTime) {
      formValues.ProductionTime = dayjs.unix(Number(modParams.ProductionTime));
    }

    // Set form values without triggering onChange
    if (Object.keys(formValues).length > 0) {
      formFilter.setFieldsValue(formValues);
    }
  }, [params]);

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        {/* Filter part */}
        <div className="mb-4">
          <Form
            className=""
            form={formFilter}
            onValuesChange={handleFormChange}
          >
            <div className="mt-2">
              <Row gutter={[16, 16]}>
                {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Input
                  className="input_text_3 mr10 !h-full"
                  name="ProductionNo"
                  placeholder="No"
                  value={filter?.ProductionNo}
                  onChange={(e) => {
                    const { value } = e.target;
                    // Allow only positive integers or empty string
                    if (/^[1-9]\d*$/.test(value) || value === "") {
                      handleFilterChange(e);
                    }
                  }}
                />
              </Col> */}
                <Col
                  xs={24}
                  sm={24}
                  md={12}
                  lg={8}
                  xl={6}
                  className="!h-[32px]"
                >
                  <Input
                    className="input_text_3 mr10 h-full"
                    placeholder="Name"
                    value={filter.ProductionName}
                    onChange={handleFilterChange}
                    name="ProductionName"
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <CustomDateTimePicker
                    name="ProductionTime"
                    placeholder="Production Time"
                    format="YYYY-MM-DD HH:mm" // 24-hour format
                  />
                </Col>
                {/* {![4, 5, 6].includes(logData?.role_value) && (
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    showSearch
                    placeholder="OEM"
                    optionFilterProp="OemNo"
                    value={
                      filter.OemNo || typeof filter.OemNo === "number"
                        ? Number(filter.OemNo)
                        : null
                    }
                    onChange={(value) => setFilter({ ...filter, OemNo: value })}
                    // onSearch={onSearch}
                    options={modifiedOems}
                    style={{ width: "100%" }}
                  />
                </Col>
              )} */}
              </Row>
            </div>

            <div className="w-full overflow-x-auto">
              <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
                <div className="flex items-center">
                  <Button
                    type="primary"
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={handleFilter}
                  >
                    <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                    Apply
                  </Button>
                  <Button
                    className="reset-button"
                    onClick={() => {
                      formFilter.resetFields();
                      handleReset();
                    }}
                  >
                    <HiArrowPath className="!text-[14px] font-medium" />
                    Reset
                  </Button>
                  <Button
                    type="primary"
                    className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                    onClick={() => navigate("/production/add")}
                  >
                    <PlusOutlined />
                    Add
                  </Button>
                  <Button
                    type="primary"
                    disabled={selectedRowKeys?.length === 0 ? true : false}
                    className="mr-2 w-[104px] h-[30px] font-medium"
                    onClick={() => {
                      setShowModal(true);
                    }}
                  >
                    <DeleteOutlined />
                    Delete
                  </Button>
                </div>
                {hasRole(1, 2, 3) && (
                  <div>
                    <Button
                      type="primary"
                      className="w-[162px] h-[30px]"
                      onClick={() => {
                        setIsModalOpen(true);
                        form.setFieldValue("ReportFile", 1);
                      }}
                      disabled={selectedRowKeys.length === 0}
                    >
                      <FaFileAlt className="!text-[14px] font-medium" />
                      Production Report
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Form>
          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <GlobalTable
            columns={ProductionColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              logData?.role_value === 4 ||
                logData?.role_value === 5 ||
                logData?.role_value === 6,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            // onRow={onRow}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
      <Modal
        title={
          <div className="flex items-center bg-gray-200 p-2 rounded-md">
            <FaFileAlt className="text-[14px] text-black pl-1 " />
            <span className="text-[14px] font-[Poppins]   ml-2">
              <p>Production Report</p>
            </span>
          </div>
        }
        open={isModalOpen}
        closable={false}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={580}
        centered
        destroyOnClose
      >
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={() => {}}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="bg-white rounded-[4px] overflow-hidden">
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col span={24}>
                  <CustomSelect
                    label="Report File"
                    name="ReportFile"
                    options={[
                      { label: "CSV", value: 1 },
                      { label: "PDF", value: 2 },
                    ]}
                    placeholder="Please select"
                    required
                    allowClear={false}
                    defaultValue={reportFile}
                    onChange={(_, value) => {
                      setReportFile(value);
                    }}
                  />
                </Col>
              </Row>
            </div>
            <div className="flex justify-center space-x-2 mt-2.5">
              <Button
                type="primary"
                className="mr-2 min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
                onClick={() => {
                  setShouldFetch(true);
                  setTimeout(() => {
                    handleExportClick(form.getFieldValue("ReportFile"));
                  }, 200);
                }}
                loading={exportLoading}
              >
                <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                <span>Apply</span>
              </Button>

              <Button
                className="reset-button"
                onClick={() => setIsModalOpen(false)}
              >
                <CloseOutlined />
                <span>Cancel</span>
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
};
